import {Box, Button, Dialog, DialogContent, DialogTitle, List, ListItemButton, ListItemIcon, ListItemText, TextField}
    from "@mui/material";
import {ChangeEventHandler, KeyboardEventHandler, useCallback, useContext, useEffect, useState} from "react";
import {connect} from "react-redux";
import {selectLocks, selectNetwork} from "../../redux/selectors/firebase";
import {NetworkData} from "../../fstn/network";
import makeStyles from "@mui/styles/makeStyles";
import StateLock, {Locks} from "../navbar/StateLock";
import {selectProject, selectUserId} from "../../redux/selectors/editor";
import {FirebaseContext} from "../firebase/FirebaseProvider";

const useStyles = makeStyles((theme) => ({
    found: {
        height: "90%",
        marginRight: theme.spacing(1),
        overflowY: "auto",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    menuItemText: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    selectedMenuItem: {
        backgroundColor: theme.palette.primary.main
    }
}));

interface IFindDialog {
    locks: Locks
    network: NetworkData
    onClose: () => any
    project: string
    userId: string
}
function FindDialog({locks, network, onClose, project, userId}: IFindDialog) {
    const classes = useStyles();
    const {removeLock, setLock, setScript} = useContext(FirebaseContext);
    const [findString, setFindString]= useState("");
    const [find, setFind]= useState("");
    const [found, setFound]= useState<{[state:string]: string}>({});
    const [replace, setReplace]= useState("");
    const [replacing, setReplacing]= useState(false);
    const [selected, setSelected]= useState(-1);
    const canReplaceAll = useCallback(() => {
        if (replace !== "" && ! replacing) {
            return Object.keys(found)
                .filter(state => locks[state] === undefined || locks[state].user === userId).length > 0;
        }
        return false;
    }, [found, locks, replace, replacing, userId]);
    const canSelectedBeReplaced = useCallback(() => {
        if (replace !== "" && selected >= 0 && ! replacing) {
            const state = Object.keys(found)[selected];
            const lock = locks[state];

            return lock === undefined || lock.user === userId;
        }
        return false;
    }, [found, locks, replace, replacing, selected, userId]);
    const getFound = useCallback((find: string) => {
        return Object.fromEntries(Object.entries(network.states)
            .filter(([, state]) => state.script.includes(find) || state.name.includes(find))
            .map(([key, state]) => [key, state.script]));
    }, [network.states]);
    const handleFindChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setFindString(target.value);
    const handleFindClick = () => {
        if (findString !== "") {
            const found = getFound(findString)

            setFind(findString);
            if (Object.keys(found).length) {
                setSelected(0);
            } else {
                setSelected(-1);
            }
            setFound(found);
        } else {
            setFind("");
            setFound({});
            setSelected(-1);
        }
    };
    const handleFindKeyDown: KeyboardEventHandler<HTMLInputElement> = ({key}) => {
        if (key.toLowerCase() === "enter") {
            handleFindClick();
        }
    }
    const handleFoundClick = (index: number) => () => setSelected(index);
    const handleReplaceChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setReplace(target.value);
    const handleReplaceAllClick = () => {
        const unlocked = Object.keys(found).filter(state => locks[state] === undefined || locks[state].user === userId);

        setReplacing(true);
        Promise.all(unlocked.map((state) =>
            setLock(project, state, userId).then(() => 
                setScript(project, state, found[state], found[state].replaceAll(find, replace), userId).then(() =>
                    removeLock(project, state).catch(console.error)
                )
            ))
        ).then(() => {
            setFound({});
            setSelected(- 1);
            setReplacing(false);
        });
    }
    const handleReplaceClick = () => {
        if (replace !== "" || selected >= 0) {
            const [state, script] = Object.entries(found)[selected];

            setReplacing(true);
            setLock(project, state, userId).then(() =>
                setScript(project, state, script, script.replaceAll(find, replace), userId).then(() => {
                    removeLock(project, state).then(() => {
                        const removed = found;

                        setReplacing(false);
                        delete removed[state];
                        setFound(removed);
                        if (Object.keys(removed).length === selected) {
                            // Replaced last in the list so set to the previous
                            setSelected(selected - 1);
                        }
                    });
                })
            );
        }
    }

    useEffect(() => {
        if (find !== "") {
            setFound(getFound(find));
        }
    }, [find, getFound, network.states]);
    return (
        <Dialog fullWidth open={true} onClose={onClose}>
            <DialogTitle>Find and Replace in Node Script</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" height="80vh" m={1}>
                    <Box display="flex" m={1}>
                        <TextField fullWidth label="Find" onChange={handleFindChange} onKeyDown={handleFindKeyDown}
                                   size="small" value={findString}/>
                        <Button disabled={findString === ""} onClick={handleFindClick} sx={{width:100}}>Find</Button>
                    </Box>
                    <Box display="flex" m={1}>
                        <TextField fullWidth label="Replace" onChange={handleReplaceChange} size="small" value={replace}/>
                        <Box width={100}/>
                    </Box>
                    <Box className={classes.found}>
                        <List dense>
                            {Object.entries(found).map(([key, text], index) =>
                                <ListItemButton classes={{
                                    selected:classes.selectedMenuItem
                                }} key={key} onClick={handleFoundClick(index)} selected={index===selected}>
                                    <ListItemIcon>
                                        <StateLock project={project} size="small" state={key}/>
                                    </ListItemIcon>
                                    <ListItemText classes={{root: classes.menuItemText}} title={text}>
                                        {text.substr(Math.max(text.indexOf(findString) - 40, 0))}
                                    </ListItemText>
                                </ListItemButton>
                            )}
                        </List>
                    </Box>
                    <Box display="flex">
                        <Button disabled={! canSelectedBeReplaced()} onClick={handleReplaceClick}>
                            Replace
                        </Button>
                        <Button disabled={! canReplaceAll()} onClick={handleReplaceAllClick}>
                            Replace All
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
const mapStateToProps = (store: never) => ({
    locks: selectLocks(store) || {},
    network: selectNetwork(store),
    project: selectProject(store),
    userId: selectUserId(store)
});

export default connect(mapStateToProps, {
})(FindDialog);
